import { useState, useEffect, useCallback, useRef } from "react";

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

declare global {
  interface WindowEventMap {
    beforeinstallprompt: BeforeInstallPromptEvent;
  }
}

export function usePwaInstall() {
  const [deferredPrompt, setDeferredPrompt] =
    useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);
  const [canInstall, setCanInstall] = useState(false);
  const promptRef = useRef<BeforeInstallPromptEvent | null>(null);

  useEffect(() => {
    // Detect platform
    const ua = navigator.userAgent;
    const ios =
      /iPad|iPhone|iPod/.test(ua) ||
      (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
    const android = /Android/.test(ua);
    setIsIOS(ios);
    setIsAndroid(android);

    // Check if already installed as PWA
    const isStandalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      (window.navigator as unknown as { standalone?: boolean }).standalone === true;
    setIsInstalled(isStandalone);

    if (isStandalone) {
      setCanInstall(false);
      return;
    }

    // iOS can always "install" via Add to Home Screen (manual process)
    if (ios) {
      setCanInstall(true);
      return;
    }

    // Android/Chrome: Listen for beforeinstallprompt
    const handler = (e: BeforeInstallPromptEvent) => {
      e.preventDefault();
      promptRef.current = e;
      setDeferredPrompt(e);
      setCanInstall(true);
    };

    window.addEventListener("beforeinstallprompt", handler);

    // Check if installed via display-mode change
    const mediaQuery = window.matchMedia("(display-mode: standalone)");
    const mediaHandler = (e: MediaQueryListEvent) => {
      if (e.matches) {
        setIsInstalled(true);
        setCanInstall(false);
      }
    };
    mediaQuery.addEventListener("change", mediaHandler);

    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      mediaQuery.removeEventListener("change", mediaHandler);
    };
  }, []);

  const installPwa = useCallback(async (): Promise<boolean> => {
    const prompt = promptRef.current;
    if (!prompt) return false;

    try {
      await prompt.prompt();
      const { outcome } = await prompt.userChoice;
      if (outcome === "accepted") {
        setIsInstalled(true);
        setCanInstall(false);
        promptRef.current = null;
        setDeferredPrompt(null);
        return true;
      }
      return false;
    } catch {
      return false;
    }
  }, []);

  const dismissInstall = useCallback(() => {
    // Mark as dismissed in localStorage so we don't show the banner again
    localStorage.setItem("ayu-pwa-install-dismissed", "true");
  }, []);

  const wasDismissed = useCallback(() => {
    return localStorage.getItem("ayu-pwa-install-dismissed") === "true";
  }, []);

  return {
    canInstall,
    isInstalled,
    isIOS,
    isAndroid,
    installPwa,
    dismissInstall,
    wasDismissed,
    hasNativePrompt: !!deferredPrompt,
  };
}
